import { ref, computed } from '@vue/composition-api'
import moment from 'moment'
import { minBy, maxBy, sortBy } from 'lodash'

const Period = {
  WEEK: '1w',
  WEEKx2: '2w',
  MONTH: '1m',
  ALL_TIME: 'all',
}

export default function useDates({ program, allApplications, DEBUG }) {
  const now = computed(() => {
    return moment()
  })
  const currentDate = ref(moment())
  const period = ref(Period.ALL_TIME)
  const setPeriod = (value) => {
    if (value !== period.value) {
      period.value = value
      currentDate.value = moment()
    }
  }
  const resetNow = () => {
    currentDate.value = moment()
  }

  // if true, start of the period would be Monday if weeks or 1st day of the month
  // otherwise it would be simply (now - period)
  const useFullPeriods = ref(false)

  const startDate = computed(() => {
    if (useFullPeriods.value) {
      if (period.value === Period.WEEK) {
        return currentDate.value.clone().isoWeekday(1).startOf('day')
      } else if (period.value === Period.WEEKx2) {
        return currentDate.value
          .clone()
          .isoWeekday(1)
          .startOf('day')
          .subtract(moment.duration(1, 'week'))
      } else if (period.value === Period.MONTH) {
        return currentDate.value.clone().startOf('month')
      } else {
        if (program.value && program.value.activated_at) {
          return moment(program.value.activated_at).startOf('day')
        } else {
          return currentDate.value.clone().startOf('year')
        }
      }
    } else {
      if (period.value === Period.WEEK) {
        return currentDate.value
          .clone()
          .subtract(moment.duration(1, 'week'))
          .add(moment.duration(1, 'day'))
          .startOf('day')
      } else if (period.value === Period.WEEKx2) {
        return currentDate.value
          .clone()
          .subtract(moment.duration(2, 'week'))
          .add(moment.duration(1, 'day'))
          .startOf('day')
      } else if (period.value === Period.MONTH) {
        return currentDate.value
          .clone()
          .subtract(moment.duration(1, 'month'))
          .add(moment.duration(1, 'day'))
          .startOf('day')
      } else {
        if (program.value && program.value.activated_at) {
          return moment(program.value.activated_at).startOf('day')
        } else if (allApplications.value.length) {
          const firstApplication = minBy(allApplications.value, 'created_at')
          return moment(firstApplication.created_at).startOf('day')
        } else {
          return currentDate.value.clone().startOf('year')
        }
      }
    }
  })

  const endDate = computed(() => {
    if (period.value === Period.ALL_TIME) {
      if (useFullPeriods.value) {
        return currentDate.value.clone().endOf('week').isoWeekday(7)
      } else if (program.value && program.value.deactivated_at) {
        return moment(program.value.deactivated_at).endOf('day')
      } else if (allApplications.value.length) {
        const lastApplication = maxBy(allApplications.value, 'created_at')
        return moment(lastApplication.created_at).endOf('day')
      } else {
        return currentDate.value.clone().endOf('day')
      }
    } else {
      const delta = getDateDeltaForPeriod(period.value)
      const ts = startDate.value.clone().add(delta)
      ts.subtract(moment.duration(1, 'second'))
      return ts
    }
  })

  const hasPreviousPeriod = computed(() => {
    if (!program.value.activated_at) {
      return false
    }
    const programStartDate = moment(program.value.activated_at).toISOString()
    if (DEBUG) {
      console.log(' => hasPreviousPeriod')
      const L = [
        {
          name: 'program.activated_at',
          value: programStartDate.substring(0, 10),
        },
        {
          name: '         currentDate',
          value: currentDate.value.toISOString().substring(0, 10),
        },
      ]
      sortBy(L, 'value').forEach((obj) => {
        console.log(`    ${obj.name}: ${obj.value}`)
      })
    }

    if (period.value === Period.ALL_TIME) {
      return false
    } else {
      return programStartDate < startDate.value.toISOString()
    }
  })
  const hasNextPeriod = computed(() => {
    if (DEBUG) {
      console.log(' => hasNextPeriod')
      const L = [
        { name: '  startDate', value: startDate.value.toISOString() },
        { name: '        now', value: now.value.toISOString() },
        { name: '    endDate', value: endDate.value.toISOString() },
      ]
      sortBy(L, 'value').forEach((obj) => {
        console.log(`    ${obj.name}: ${obj.value.substring(0, 10)}`)
      })
    }

    if (endDate.value.toISOString() < now.value.toISOString()) {
      return true
    } else {
      return false
    }
  })
  const nextDateRange = () => {
    const delta = getDateDeltaForPeriod(period.value)
    currentDate.value = currentDate.value.clone().add(delta)
  }
  const prevDateRange = () => {
    const delta = getDateDeltaForPeriod(period.value)
    currentDate.value = currentDate.value.clone().subtract(delta)
  }

  function getDateDeltaForPeriod(value) {
    const delta = {
      [Period.WEEK]: moment.duration(1, 'week'),
      [Period.WEEKx2]: moment.duration(2, 'week'),
      [Period.MONTH]: moment.duration(1, 'month'),
    }[value]
    if (delta === undefined) {
      throw new Error('Delta is undefined')
    } else {
      return delta
    }
  }

  const datesRange = computed(() => {
    const result = []
    const ts = startDate.value.clone()

    while (ts <= endDate.value) {
      const day = ts.format('Y-MM-DD')
      result.push(day)
      ts.add(moment.duration(1, 'day'))
    }

    return result
  })

  const periodLabel = computed(() => {
    return {
      [Period.WEEK]: '1 week',
      [Period.WEEKx2]: '2 weeks',
      [Period.MONTH]: '1 month',
      [Period.ALL_TIME]: 'All time',
    }[period.value]
  })

  return {
    Period,
    period,
    setPeriod,
    periodLabel,
    prevDateRange,
    nextDateRange,
    resetNow,
    getDateDeltaForPeriod,
    currentDate,
    hasNextPeriod,
    hasPreviousPeriod,
    startDate,
    endDate,
    datesRange,
  }
}
